import { useState, useEffect } from 'react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Smartphone } from "lucide-react";
import { pushNotificationService } from '@/services/pushNotificationService';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

interface PushNotificationsSectionProps {
  isEditing?: boolean;
}

const PushNotificationsSection = ({ isEditing = false }: PushNotificationsSectionProps) => {
  const { user, profile } = useAuth();
  // Type assertion for new column not yet in generated types
  const sellerProfile = profile as any;
  const [isEnabled, setIsEnabled] = useState(sellerProfile?.push_notifications_enabled ?? true);
  const [isLoading, setIsLoading] = useState(false);
  const isPushAvailable = pushNotificationService.isAvailable();

  useEffect(() => {
    if (sellerProfile) {
      setIsEnabled(sellerProfile.push_notifications_enabled ?? true);
    }
  }, [profile]);

  const handleToggle = async (checked: boolean) => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // If enabling, also request permission and register token
      if (checked && isPushAvailable) {
        const initialized = await pushNotificationService.initialize();
        if (initialized) {
          await pushNotificationService.registerToken(user.id, 'seller');
        }
      }

      // Update the database
      const success = await pushNotificationService.updatePushEnabled(
        user.id,
        'seller',
        checked
      );

      if (success) {
        setIsEnabled(checked);
        toast.success(checked ? 'Push notifications enabled' : 'Push notifications disabled');
      } else {
        toast.error('Failed to update push notification settings');
      }
    } catch (error) {
      logger.error('[PushNotificationsSection] Error toggling push:', error);
      toast.error('Failed to update push notification settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if not on a native platform
  if (!isPushAvailable) {
    return null;
  }

  return (
    <div className="flex items-center justify-between rounded-lg border p-4 mt-4">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-muted-foreground" />
          <Label className="text-base font-medium">Push Notifications</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Receive instant alerts on your device for new offers and updates
        </p>
      </div>
      <div className="flex items-center gap-2">
        {isEnabled ? (
          <Bell className="h-4 w-4 text-godyspo-gold" />
        ) : (
          <BellOff className="h-4 w-4 text-muted-foreground" />
        )}
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={!isEditing || isLoading}
        />
      </div>
    </div>
  );
};

export default PushNotificationsSection;
